// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Organization } from '@medplum/fhirtypes';
import type { Cms1500ProviderRole } from '@polarissleep/shared';
import {
  blankCms1500OtherInsured,
  blankCms1500Provider,
  CMS1500_ATTACHMENT_TRANSMISSIONS,
  CMS1500_ATTACHMENT_TYPES,
  CMS1500_CLAIM_CODES,
  CMS1500_DATES_14,
  CMS1500_DATES_15,
  CMS1500_DATES_15_MORE,
  CMS1500_INSURANCE_TYPES,
  CMS1500_LETTERS,
  CMS1500_NOTE_QUALIFIERS,
  CMS1500_OTHER_CLAIM_QUALIFIERS,
  CMS1500_PROVIDER_ROLES,
  CMS1500_RESP_LEVELS,
  CMS1500_RESUBMISSION_CODES,
  CMS1500_STATES,
  cms1500TotalCharge,
} from '@polarissleep/shared';
import { IconChevronDown, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import {
  AddressFields,
  classes,
  cols,
  cx,
  DateIn,
  Field,
  Head,
  MoneyIn,
  NameFields,
  Radios,
  SEX_OPTIONS,
  SelectIn,
  TextIn,
  useCms1500Editor,
  YesNo,
} from './Cms1500Fields';

const STEDI_PAYER_NETWORK_SYSTEM = 'https://www.stedi.com/healthcare/network';
const PROVIDER_FIELDS = ['last', 'first', 'middle', 'suffix', 'npi', 'lic', 'g2', 'upin', 'taxonomy'] as const;
const PARTY_TYPES: [string, string][] = [
  ['ind', 'Individual'],
  ['org', 'Organization'],
];

/** Common sleep-medicine ICD-10-CM codes offered as suggestions in box 21. */
const ICD_SUGGESTIONS: [string, string][] = [
  ['G47.33', 'Obstructive sleep apnea (adult) (pediatric)'],
  ['G47.30', 'Sleep apnea, unspecified'],
  ['G47.31', 'Primary central sleep apnea'],
  ['G47.10', 'Hypersomnia, unspecified'],
  ['G47.00', 'Insomnia, unspecified'],
  ['G47.411', 'Narcolepsy with cataplexy'],
  ['G47.419', 'Narcolepsy without cataplexy'],
  ['G47.61', 'Periodic limb movement disorder'],
  ['R06.83', 'Snoring'],
  ['R40.0', 'Somnolence'],
  ['I10', 'Essential (primary) hypertension'],
  ['E66.9', 'Obesity, unspecified'],
  ['F51.01', 'Primary insomnia'],
];

export function stediPayerId(organization: Organization | undefined): string {
  return organization?.identifier?.find((identifier) => identifier.system === STEDI_PAYER_NETWORK_SYSTEM)?.value ?? '';
}

interface PayerRowProps {
  payers: Organization[];
  payerOrganizationId?: string;
  onPayerOrganizationChange: (organization: Organization) => void;
}

/** Send · Stedi payer · Claim identifier */
export function PayerRow({ payers, payerOrganizationId, onPayerOrganizationChange }: PayerRowProps): JSX.Element {
  const { form, update, errorFor, touch, readOnly } = useCms1500Editor();
  const payerError = errorFor('payer.id');
  return (
    <div className={cx.row} style={cols('--c', 3)}>
      <div className={cx.cell}>
        <Head title="Send" />
        <Radios
          path="send"
          label="Send"
          options={[
            ['electronic', 'Electronically'],
            ['mail', 'By mail'],
          ]}
        />
      </div>
      <div className={cx.cell}>
        <Head title="Stedi payer" req />
        <div className={classes(cx.selectWrap, cx.search)}>
          <IconSearch />
          <select
            className={classes(cx.input, payerError && !form.payer.id && cx.bad)}
            value={payerOrganizationId ?? ''}
            disabled={readOnly}
            aria-label="Stedi payer"
            data-claim-field="payer.id"
            onChange={(event) => {
              const payer = payers.find((candidate) => candidate.id === event.currentTarget.value);
              if (!payer) {
                return;
              }
              onPayerOrganizationChange(payer);
              update((draft) => {
                draft.payer = { id: stediPayerId(payer), name: payer.name ?? '' };
              });
              touch('payer.id');
            }}
          >
            <option value="">Select the payer to send this claim to</option>
            {payers.map((payer) => (
              <option key={payer.id} value={payer.id}>
                {payer.name}
              </option>
            ))}
          </select>
          <IconChevronDown />
        </div>
        {form.payer.id ? (
          <div className={cx.hint}>
            Payer ID <b>{form.payer.id}</b> ·{' '}
            {form.send === 'mail' ? 'Stedi prints and mails a CMS-1500' : 'sent as an 837P'}
          </div>
        ) : (
          <Field label="Stedi payer ID" req>
            <TextIn path="payer.id" label="Stedi payer ID" placeholder="This payer has no Stedi ID on file" />
          </Field>
        )}
      </div>
      <div className={cx.cell}>
        <Head
          title="Claim identifier"
          req
          help="Chargeable bills the payer. Reporting sends encounter data only. Subrogation recovers payment from another responsible party."
        />
        <Radios
          path="claimType"
          stack
          label="Claim identifier"
          options={[
            ['chargeable', 'Chargeable'],
            ['reporting', 'Reporting'],
            ['subrogation', 'Subrogation'],
          ]}
        />
      </div>
    </div>
  );
}

/** 1 Insurance type · 1a Insured ID number */
export function InsuranceRow(): JSX.Element {
  return (
    <div className={cx.row} style={cols('--c', 3)}>
      <div className={classes(cx.cell, cx.span2)}>
        <Head
          n="1"
          title="Insurance type"
          req
          help="The program that covers the patient. Use Other for commercial and group health plans."
        />
        <Radios path="insType" options={CMS1500_INSURANCE_TYPES} label="Insurance type" />
      </div>
      <div className={cx.cell}>
        <Head
          n="a"
          title="Insured ID number"
          help="The member ID exactly as it appears on the insured's card, and whether this payer is primary, secondary, or tertiary."
        />
        <div className={cx.grid}>
          <Field label="Payment responsibility level" req>
            <SelectIn path="respLevel" options={CMS1500_RESP_LEVELS} label="Payment responsibility level" />
          </Field>
          <Field label="Value" req>
            <TextIn path="memberId" maxLength={80} label="Insured ID number" />
          </Field>
        </div>
      </div>
    </div>
  );
}

/** 2 Patient name · 3 Patient DOB, Sex · 4 Insured name */
export function NamesRow(): JSX.Element {
  const { form } = useCms1500Editor();
  const self = form.rel === 'self';
  return (
    <div className={cx.row} style={cols('--c', 3)}>
      <div className={cx.cell}>
        <Head n="2" title="Patient name" help="Required when the patient is not the insured (box 6 isn't Self)." />
        <NameFields prefix="pat" requireLast={!self} requireFirst={!self} />
      </div>
      <div className={cx.cell}>
        <Head n="3" title="Patient DOB, Sex" help="Required when the patient is not the insured." />
        <Field label="Date of birth" req={!self}>
          <DateIn path="pat.dob" label="Patient date of birth" />
        </Field>
        <Field label="Sex" req={!self}>
          <Radios path="pat.sex" options={SEX_OPTIONS} label="Patient sex" />
        </Field>
      </div>
      <div className={cx.cell}>
        <Head n="4" title="Insured name" help="The subscriber — the person who holds the policy." />
        <NameFields prefix="ins" requireLast requireFirst />
      </div>
    </div>
  );
}

/** 5 Patient address · 6 Relationship · 7 Insured address */
export function AddressRow(): JSX.Element {
  const { form } = useCms1500Editor();
  const self = form.rel === 'self';
  return (
    <div className={cx.row} style={cols('--c', 3)}>
      <div className={cx.cell}>
        <Head n="5" title="Patient address" help="Required when the patient is not the insured." />
        <AddressFields prefix="pat" required={!self} />
      </div>
      <div className={classes(cx.cell, cx.cellFill)}>
        <Head
          n="6"
          title="Patient Relationship to Insured"
          help="Self sends the patient as the subscriber. Any other value sends a separate patient loop and requires boxes 2, 3, and 5."
        />
        <Radios
          path="rel"
          label="Patient relationship to insured"
          options={[
            ['self', 'Self'],
            ['spouse', 'Spouse'],
            ['child', 'Child'],
            ['other', 'Other'],
          ]}
        />
        <div className={cx.fill} />
      </div>
      <div className={cx.cell}>
        <Head n="7" title="Insured address" help="Required when the patient is the insured." />
        <AddressFields prefix="ins" required={self} />
      </div>
    </div>
  );
}

/** 9 Other insured · 10 Condition related to · 11 Insured policy */
export function CoverageRow(): JSX.Element {
  const { form, update, errorFor, readOnly } = useCms1500Editor();
  const self = form.rel === 'self';
  const codes = form.cond.codes;
  return (
    <div className={cx.row} style={cols('--c', 3)}>
      <div className={cx.cell}>
        {form.other ? (
          <>
            <Head
              n="9"
              title="Other insured's name"
              extra={
                <button
                  type="button"
                  className={cx.link}
                  onClick={() =>
                    update((draft) => {
                      draft.other = null;
                    })
                  }
                >
                  Remove
                </button>
              }
            />
            <NameFields prefix="other" requireLast requireFirst />
            <Head n="a" title="Other insured's policy or group number" req />
            <TextIn path="other.policy" maxLength={50} label="Other insured's policy or group number" />
            <Head n="d" title="Insurance plan or program name" req />
            <TextIn path="other.plan" maxLength={60} label="Other insurance plan name" />
            <div className={cx.grid}>
              <Field label="Other payer ID" req>
                <TextIn path="other.payerId" maxLength={80} label="Other payer ID" />
              </Field>
              <Field label="Other insured member ID" req>
                <TextIn path="other.memberId" maxLength={80} label="Other insured member ID" />
              </Field>
            </div>
            <Field label="Other payer responsibility level" req>
              <SelectIn path="other.respLevel" options={CMS1500_RESP_LEVELS} label="Other payer responsibility level" />
            </Field>
          </>
        ) : (
          <>
            <div className={cx.sec}>Other insured</div>
            {!readOnly && (
              <button
                type="button"
                className={cx.add}
                onClick={() =>
                  update((draft) => {
                    draft.other = blankCms1500OtherInsured();
                  })
                }
              >
                <IconPlus />
                Add other insured
              </button>
            )}
          </>
        )}
      </div>
      <div className={cx.cell}>
        <Head
          n="10"
          title="Is patient's condition related to"
          help="Tells the payer whether another party (employer, auto insurer) may be liable."
        />
        <div className={cx.subq}>
          <div className={cx.subh}>
            <span className={cx.num}>a</span>Employment?
          </div>
          <YesNo path="cond.emp" label="Employment" />
        </div>
        <div className={cx.grid}>
          <div className={cx.subq}>
            <div className={cx.subh}>
              <span className={cx.num}>b</span>Auto accident?
            </div>
            <YesNo path="cond.auto" label="Auto accident" />
          </div>
          <Field label="State of the auto accident" req={form.cond.auto === 'yes'}>
            <SelectIn path="cond.autoState" options={CMS1500_STATES} label="State of the auto accident" />
          </Field>
        </div>
        <div className={cx.subq}>
          <div className={cx.subh}>
            <span className={cx.num}>c</span>Other?
          </div>
          <YesNo path="cond.other" label="Other accident" />
        </div>
        <Head n="10d" title="Claim codes" help="Condition codes, mostly used by Medicaid and workers' compensation." />
        <div className={classes(cx.codes, errorFor('cond.codes') && cx.bad)} data-claim-field="cond.codes">
          {codes.map((code, index) => (
            <span key={code} className={cx.chip}>
              {code}
              {!readOnly && (
                <button
                  type="button"
                  aria-label={`Remove ${code}`}
                  onClick={() =>
                    update((draft) => {
                      draft.cond.codes.splice(index, 1);
                    })
                  }
                >
                  <IconX />
                </button>
              )}
            </span>
          ))}
          {codes.length < 4 && !readOnly && (
            <div className={classes(cx.selectWrap, cx.codeSelect)}>
              <select
                className={cx.input}
                value=""
                aria-label="Add a claim code"
                onChange={(event) => {
                  const code = event.currentTarget.value;
                  if (code) {
                    update((draft) => {
                      draft.cond.codes.push(code);
                    });
                  }
                }}
              >
                <option value="">{codes.length ? 'Add another code' : ''}</option>
                {CMS1500_CLAIM_CODES.filter(([value]) => !codes.includes(value)).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <IconChevronDown />
            </div>
          )}
        </div>
        <div className={cx.hint}>Add up to 4 codes</div>
      </div>
      <div className={cx.cell}>
        <Head n="11" title="Insured policy group or FECA number" help="The group number on the insured's card." />
        <TextIn path="ins.group" maxLength={50} label="Insured policy group or FECA number" />
        <Head n="a" title="Insured date of birth and sex" help="Required when the patient is the insured." />
        <Field label="Date of birth" req={self}>
          <DateIn path="ins.dob" label="Insured date of birth" />
        </Field>
        <Field label="Sex" req={self}>
          <Radios path="ins.sex" options={SEX_OPTIONS} label="Insured sex" />
        </Field>
        <Head
          n="b"
          title="Other claim ID"
          help="A claim number assigned by the payer or agency, e.g. a workers' comp claim number."
        />
        <div className={classes(cx.grid, cx.qv)}>
          <Field label="Qualifier">
            <SelectIn path="ins.ocq" options={CMS1500_OTHER_CLAIM_QUALIFIERS} label="Other claim ID qualifier" />
          </Field>
          <Field label="Value">
            <TextIn path="ins.ocid" maxLength={50} white label="Other claim ID" />
          </Field>
        </div>
        <Head n="c" title="Insurance plan or program name" />
        <TextIn path="ins.planName" maxLength={60} label="Insurance plan or program name" />
      </div>
    </div>
  );
}

/** 12 Patient signature · 13 Insured signature */
export function SignatureRow(): JSX.Element {
  const [show12, setShow12] = useState(false);
  const [show13, setShow13] = useState(false);
  return (
    <div className={cx.row} style={cols('--c', 3)}>
      <div className={classes(cx.cell, cx.span2)}>
        <Head
          n="12"
          title="Patient or authorized person's signature"
          req
          help="Yes means a signed release is on file. Informed Consent means the patient consented but no signature is on file."
        />
        <Radios
          path="sig12"
          label="Patient or authorized person's signature"
          options={[
            ['yes', 'Yes'],
            ['informed', 'Informed Consent'],
          ]}
        />
        <p className={cx.p}>
          I authorize the release of any medical or other information necessary to process this claim. I also request
          payment of government benefits either to myself or to the party who accepts assignment.
        </p>
        {show12 && (
          <p className={classes(cx.p, cx.muted)}>
            Choosing Yes sends release-of-information code Y. A dated authorization must be kept in the patient's record
            — the intake consent signed at onboarding covers this.
          </p>
        )}
        <button type="button" className={cx.link} onClick={() => setShow12(!show12)}>
          {show12 ? 'Hide statements' : 'Read statements'}
        </button>
      </div>
      <div className={cx.cell}>
        <Head
          n="13"
          title="Insured's or authorized person's signature"
          req
          help="Assignment of benefits — whether the payer may pay the provider directly."
        />
        <Radios
          path="sig13"
          label="Insured's or authorized person's signature"
          options={[
            ['yes', 'Yes'],
            ['no', 'No'],
            ['na', 'Not Applicable'],
          ]}
        />
        <p className={cx.p}>
          I authorize payment of medical benefits to the undersigned physician or supplier for services described below.
        </p>
        {show13 && (
          <p className={classes(cx.p, cx.muted)}>
            No or Not Applicable means the payer pays the insured, and the patient is billed for the full charge.
          </p>
        )}
        <button type="button" className={cx.link} onClick={() => setShow13(!show13)}>
          {show13 ? 'Hide statements' : 'Read statements'}
        </button>
      </div>
    </div>
  );
}

interface ClinicalRowProps {
  showAllDates: boolean;
  onShowAllDatesChange: (value: boolean) => void;
  providerTab: Cms1500ProviderRole;
  onProviderTabChange: (role: Cms1500ProviderRole) => void;
}

/** 14–16, 18 Clinical dates · 17 Providers */
export function ClinicalRow({
  showAllDates,
  onShowAllDatesChange,
  providerTab,
  onProviderTabChange,
}: ClinicalRowProps): JSX.Element {
  const { form, update, errorFor, readOnly } = useCms1500Editor();
  const dates15 = showAllDates ? [...CMS1500_DATES_15, ...CMS1500_DATES_15_MORE] : CMS1500_DATES_15;
  const role = providerTab;
  const provider = form.prov[role];
  const label = CMS1500_PROVIDER_ROLES.find(([candidate]) => candidate === role)?.[1] ?? '';
  const filled = PROVIDER_FIELDS.some((field) => provider[field].trim());
  return (
    <div className={cx.row} style={cols('--c', 2)}>
      <div className={cx.cell}>
        <div className={cx.sec}>Clinical dates</div>
        <Head
          n="14"
          title="Date of Current Illness, Injury, or Pregnancy (LMP)"
          help="Only one date — onset or last menstrual period."
        />
        <div className={cx.grid}>
          {CMS1500_DATES_14.map(([qualifier, text]) => (
            <Field key={qualifier} code={qualifier} label={text}>
              <DateIn path={`dates.${qualifier}`} label={text} />
            </Field>
          ))}
        </div>
        <Head n="15" title="Other date" />
        <div className={cx.grid}>
          {dates15.map(([qualifier, text]) => (
            <Field key={qualifier} code={qualifier} label={text}>
              <DateIn path={`dates.${qualifier}`} label={text} />
            </Field>
          ))}
        </div>
        <button type="button" className={cx.add} onClick={() => onShowAllDatesChange(!showAllDates)}>
          {showAllDates ? 'Show fewer dates' : 'Show all dates'}
          <IconChevronDown style={{ transform: showAllDates ? 'rotate(180deg)' : undefined }} />
        </button>
        <Head n="16" title="Dates patient unable to work in current occupation" />
        <div className={cx.grid}>
          <Field label="From">
            <DateIn path="unableFrom" label="Unable to work from" />
          </Field>
          <Field label="To">
            <DateIn path="unableTo" label="Unable to work to" />
          </Field>
        </div>
        <Head n="18" title="Hospitalization dates related to current services" />
        <div className={cx.grid}>
          <Field label="From">
            <DateIn path="hospFrom" label="Hospitalized from" />
          </Field>
          <Field label="To">
            <DateIn path="hospTo" label="Hospitalized to" />
          </Field>
        </div>
      </div>
      <div className={cx.cell}>
        <div className={cx.sec}>Providers or other source</div>
        <div className={cx.tabs} role="tablist">
          {CMS1500_PROVIDER_ROLES.map(([tabRole, tabLabel]) => {
            const tabProvider = form.prov[tabRole];
            const on = Boolean(tabProvider.last.trim() || tabProvider.npi.trim());
            const bad = PROVIDER_FIELDS.some((field) => errorFor(`prov.${tabRole}.${field}`));
            return (
              <button
                key={tabRole}
                type="button"
                role="tab"
                aria-selected={tabRole === role}
                className={classes(cx.tab, tabRole === role && cx.tabOn, bad && cx.tabBad)}
                onClick={() => onProviderTabChange(tabRole)}
              >
                <b>{tabRole}</b>
                {tabLabel}
                {on && <span className={cx.dot} />}
              </button>
            );
          })}
        </div>
        <Head
          n="17"
          title={`Name of ${label.toLowerCase()}`}
          help={
            role === '82'
              ? 'The clinician who performed the service. Sent on every line unless a line names its own rendering NPI.'
              : 'Enter the provider who referred, ordered, or supervised the service. Last name and NPI are required once any field is filled.'
          }
        />
        <NameFields prefix={`prov.${role}`} requireLast={filled} />
        <div className={cx.sec}>Identifiers</div>
        <Head n="a" title="Other identifiers" />
        <div className={cx.grid}>
          <Field code="0B" label="State License Number">
            <TextIn path={`prov.${role}.lic`} maxLength={50} label="State license number" />
          </Field>
          <Field code="G2" label="Provider Commercial Number">
            <TextIn path={`prov.${role}.g2`} maxLength={50} label="Provider commercial number" />
          </Field>
        </div>
        <Field code="1G" label="Provider UPIN Number">
          <TextIn path={`prov.${role}.upin`} maxLength={50} label="Provider UPIN number" />
        </Field>
        {role === '82' && (
          <Field code="PXC" label="Provider Taxonomy (optional)">
            <TextIn path="prov.82.taxonomy" maxLength={10} uppercase label="Rendering provider taxonomy" />
          </Field>
        )}
        <Head n="b" title="NPI" req={filled} />
        <TextIn path={`prov.${role}.npi`} maxLength={10} placeholder="9876543210" label={`${label} NPI`} />
        {filled && !readOnly && (
          <button
            type="button"
            className={cx.link}
            onClick={() =>
              update((draft) => {
                draft.prov[role] = blankCms1500Provider();
              })
            }
          >
            Remove {label.toLowerCase()}
          </button>
        )}
      </div>
    </div>
  );
}

/** 19a Additional claim information · 20 Outside lab */
export function NoteRow({ onGoToLines }: { onGoToLines: () => void }): JSX.Element {
  return (
    <div className={cx.row} style={cols('--c', 2)}>
      <div className={cx.cell}>
        <Head n="19a" title="Additional claim information" help="A claim-level note, up to 80 characters." />
        <div className={classes(cx.grid, cx.qv)}>
          <Field label="Qualifier">
            <SelectIn path="n19q" options={CMS1500_NOTE_QUALIFIERS} label="Note qualifier" />
          </Field>
          <Field label="Note">
            <TextIn path="n19" maxLength={80} white label="Additional claim information" />
          </Field>
        </div>
      </div>
      <div className={cx.cell}>
        <Head n="20" title="Outside lab" help="Purchased services from an outside lab are reported per line." />
        <div className={classes(cx.hint, cx.hintBig)}>
          Outside lab dollar charges are set at the service line level in{' '}
          <button type="button" className={cx.link} onClick={onGoToLines}>
            Box 24
          </button>
          .
        </div>
      </div>
    </div>
  );
}

/** 19b Claim attachments · 22 Resubmission code */
export function AttachmentResubmissionRow(): JSX.Element {
  const { form, update, readOnly } = useCms1500Editor();
  const replacing = form.resubQ === '7' || form.resubQ === '8';
  return (
    <div className={cx.row} style={cols('--c', 2)}>
      <div className={cx.cell}>
        <Head
          n="19b"
          title="Claim attachments"
          help="Tells the payer how supporting documents will arrive. Upload the files themselves under Attachments below."
        />
        {form.att.map((_, index) => (
          <div key={`att-${index}`} className={cx.att}>
            <Field label={index ? undefined : 'Report type'} req={!index}>
              <SelectIn path={`att.${index}.type`} options={CMS1500_ATTACHMENT_TYPES} label="Report type" />
            </Field>
            <Field label={index ? undefined : 'Transmission'} req={!index}>
              <SelectIn path={`att.${index}.trans`} options={CMS1500_ATTACHMENT_TRANSMISSIONS} label="Transmission" />
            </Field>
            <Field label={index ? undefined : 'Control number'}>
              <TextIn path={`att.${index}.ctrl`} maxLength={50} label="Control number" />
            </Field>
            {!readOnly && (
              <button
                type="button"
                className={cx.xb}
                aria-label="Remove attachment"
                onClick={() =>
                  update((draft) => {
                    draft.att.splice(index, 1);
                  })
                }
              >
                <IconX />
              </button>
            )}
          </div>
        ))}
        {!readOnly && (
          <button
            type="button"
            className={cx.add}
            onClick={() =>
              update((draft) => {
                draft.att.push({ type: '', trans: '', ctrl: '' });
              })
            }
          >
            <IconPlus />
            Add attachment
          </button>
        )}
      </div>
      <div className={cx.cell}>
        <Head
          n="22"
          title="Resubmission code"
          help="Use 7 to replace or 8 to void a claim the payer already accepted, with the payer's claim number."
        />
        <div className={classes(cx.grid, cx.qv)}>
          <Field label="Qualifier">
            <SelectIn path="resubQ" options={CMS1500_RESUBMISSION_CODES} label="Resubmission code" />
          </Field>
          <Field label="Original reference number" req={replacing}>
            <TextIn path="origRef" maxLength={50} label="Original reference number" />
          </Field>
        </div>
      </div>
    </div>
  );
}

/** 21 Diagnoses · 23 Prior authorization */
export function DiagnosisRow({ onGoToLines }: { onGoToLines: () => void }): JSX.Element {
  return (
    <div className={cx.row} style={cols('--c', 2)}>
      <div className={cx.cell}>
        <Head
          n="21"
          title="Diagnosis or nature of illness or injury"
          req
          help="ICD-10-CM codes, A through L, with no gaps. Service lines point at these letters."
        />
        <div className={classes(cx.grid, cx.dx)}>
          {CMS1500_LETTERS.map((letter, index) => (
            <Field key={letter} label={index ? letter : 'A (Primary diagnosis)'}>
              <DiagnosisInput index={index} />
            </Field>
          ))}
        </div>
        <datalist id="cx-icd">
          {ICD_SUGGESTIONS.map(([code, description]) => (
            <option key={code} value={code}>
              {description}
            </option>
          ))}
        </datalist>
      </div>
      <div className={cx.cell}>
        <Head n="23" title="Prior Authorization Number" />
        <div className={cx.grid}>
          <Field code="G1" label="Prior authorization number">
            <TextIn path="priorAuth" maxLength={50} label="Prior authorization number" />
            <div className={cx.hint}>
              One prior auth per claim. Add more at the service line level in{' '}
              <button type="button" className={cx.link} onClick={onGoToLines}>
                item 24
              </button>
              .
            </div>
          </Field>
          <Field code="9F" label="Referral number">
            <TextIn path="referral" maxLength={50} label="Referral number" />
          </Field>
          <Field code="X4" label="CLIA number">
            <TextIn path="clia" maxLength={10} uppercase label="CLIA number" />
          </Field>
          <Field code="EW" label="Mammography certification number">
            <TextIn path="mammo" maxLength={50} label="Mammography certification number" />
          </Field>
        </div>
      </div>
    </div>
  );
}

function DiagnosisInput({ index }: { index: number }): JSX.Element {
  const { form, setValue, errorFor, touch, readOnly } = useCms1500Editor();
  const path = `dx.${index}`;
  const message = errorFor(path);
  return (
    <>
      <div className={cx.selectWrap}>
        <input
          className={classes(cx.input, message && cx.bad)}
          list="cx-icd"
          maxLength={8}
          value={form.dx[index]}
          disabled={readOnly}
          aria-label={`Diagnosis ${CMS1500_LETTERS[index]}`}
          aria-invalid={Boolean(message)}
          autoComplete="off"
          data-claim-field={path}
          onChange={(event) => setValue(path, event.currentTarget.value)}
          onBlur={(event) => {
            setValue(path, event.currentTarget.value.trim().toUpperCase());
            touch(path);
          }}
        />
        <IconChevronDown />
      </div>
      {message && <div className={cx.error}>{message}</div>}
    </>
  );
}

/** 25 Tax ID · 26 Account number · 27 Assignment · 28 Total · 29 Amount paid */
export function TotalsRow(): JSX.Element {
  const { form } = useCms1500Editor();
  return (
    <div className={classes(cx.row, cx.row5)} style={cols('--c', 5)}>
      <div className={cx.cell}>
        <Head
          n="25"
          title="Federal tax ID number"
          req
          help="The billing provider's EIN, or SSN for a sole proprietor."
        />
        <TextIn
          path="taxId"
          maxLength={11}
          placeholder={form.taxType === 'SSN' ? '123-45-6789' : '12-3456789'}
          label="Federal tax ID number"
        />
        <Radios
          path="taxType"
          label="Tax ID type"
          options={[
            ['EIN', 'EIN'],
            ['SSN', 'SSN'],
          ]}
        />
      </div>
      <div className={cx.cell}>
        <Head
          n="26"
          title="Patient account number"
          req
          help="Sent as the 837P patient control number — the 277CA and 835 echo it back so responses find this claim. Set from the encounter, so it can't be edited."
        />
        <TextIn path="acct" maxLength={17} readOnly label="Patient account number" />
      </div>
      <div className={cx.cell}>
        <Head
          n="27"
          title="Accept assignment"
          req
          help="Whether you accept the payer's allowed amount as payment in full."
        />
        <Radios
          path="assign"
          stack
          label="Accept assignment"
          options={[
            ['yes', 'Yes'],
            ['lab', 'Clinical lab only'],
            ['no', 'No'],
          ]}
        />
      </div>
      <div className={cx.cell}>
        <Head n="28" title="Total charge" />
        <input
          className={cx.input}
          readOnly
          tabIndex={-1}
          aria-label="Total charge"
          value={cms1500TotalCharge(form).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        />
        <div className={cx.hint}>Calculated automatically based on the service lines</div>
      </div>
      <div className={cx.cell}>
        <Head
          n="29"
          title="Amount paid"
          help="Amount the patient already paid toward covered services. Left off the claim when blank or zero."
        />
        <MoneyIn path="amtPaid" label="Amount paid" />
      </div>
    </div>
  );
}

/** 31 Signature + Submitter · 32 Service facility · 33 Billing provider */
export function ProvidersRow(): JSX.Element {
  const { form } = useCms1500Editor();
  return (
    <div className={cx.row} style={cols('--c', 3)}>
      <div className={cx.cell}>
        <Head
          n="31"
          title="Signature of physician or supplier"
          req
          help="Yes certifies the rendering provider's signature is on file."
        />
        <p className={cx.p}>
          I certify that the statements on the reverse apply to this bill and are made a part thereof.
        </p>
        <Radios
          path="sig31"
          label="Signature of physician or supplier"
          options={[
            ['yes', 'Yes'],
            ['no', 'No'],
          ]}
        />
        <div className={cx.div} />
        <div className={cx.subt}>Submitter</div>
        <Radios path="sub.type" options={PARTY_TYPES} label="Submitter type" />
        {form.sub.type === 'ind' ? (
          <NameFields prefix="sub" requireLast requireFirst />
        ) : (
          <Field label="Organization name" req>
            <TextIn path="sub.org" maxLength={60} label="Submitter organization name" />
          </Field>
        )}
        <div className={cx.grid}>
          <Field label="Phone number">
            <TextIn path="sub.phone" maxLength={14} placeholder="(312) 555-1212" label="Submitter phone number" />
          </Field>
          <Field label="Fax number">
            <TextIn path="sub.fax" maxLength={14} label="Submitter fax number" />
          </Field>
        </div>
        <Field label="Email">
          <TextIn path="sub.email" maxLength={80} placeholder="name@work.com" label="Submitter email" />
        </Field>
        <Field label="ETIN">
          <TextIn path="sub.etin" maxLength={80} label="ETIN" />
        </Field>
      </div>
      <div className={cx.cell}>
        <Head
          n="32"
          title="Service facility location information"
          help="Where services were rendered, if different from the billing provider. Address must be a physical location with ZIP+4."
        />
        <Field label="Organization name">
          <TextIn path="fac.org" maxLength={60} label="Service facility name" />
        </Field>
        <AddressFields prefix="fac" />
        <Head n="a" title="NPI" />
        <TextIn path="fac.npi" maxLength={10} placeholder="9876543210" label="Service facility NPI" />
        <Head n="b" title="Other identifiers" />
        <div className={cx.grid}>
          <Field code="0B" label="State License Number">
            <TextIn path="fac.lic" maxLength={50} label="Service facility state license number" />
          </Field>
          <Field code="G2" label="Provider Commercial Number">
            <TextIn path="fac.g2" maxLength={50} label="Service facility commercial number" />
          </Field>
          <Field code="LU" label="Location Number">
            <TextIn path="fac.lu" maxLength={50} label="Service facility location number" />
          </Field>
        </div>
      </div>
      <div className={cx.cell}>
        <Head
          n="33"
          title="Billing provider info"
          help="Who gets paid. Must be a physical address (no PO Box) with a 9-digit ZIP, plus an NPI."
        />
        <Radios path="bill.type" options={PARTY_TYPES} label="Billing provider type" />
        {form.bill.type === 'ind' ? (
          <NameFields prefix="bill" requireLast requireFirst />
        ) : (
          <Field label="Organization name" req>
            <TextIn path="bill.org" maxLength={60} label="Billing provider organization name" />
          </Field>
        )}
        <AddressFields prefix="bill" required />
        <Field label="Phone number">
          <TextIn path="bill.phone" maxLength={14} placeholder="(312) 555-1212" label="Billing provider phone number" />
        </Field>
        <Head n="a" title="NPI" req />
        <TextIn path="bill.npi" maxLength={10} placeholder="9876543210" label="Billing provider NPI" />
        <Head n="b" title="Other identifiers" />
        <div className={cx.grid}>
          <Field code="0B" label="State License Number">
            <TextIn path="bill.lic" maxLength={50} label="Billing provider state license number" />
          </Field>
          <Field code="G2" label="Provider Commercial Number">
            <TextIn path="bill.g2" maxLength={50} label="Billing provider commercial number" />
          </Field>
          <Field code="LU" label="Location Number">
            <TextIn path="bill.lu" maxLength={50} label="Billing provider location number" />
          </Field>
          <Field code="ZZ" label="Provider Taxonomy">
            <TextIn path="bill.taxonomy" maxLength={10} uppercase label="Billing provider taxonomy" />
          </Field>
        </div>
      </div>
    </div>
  );
}
